/** Cards, badges, avatars, alerts and the other flat surfaces. */

import type { HTMLAttributes, ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';

import { cn } from '../../lib/cn';
import { initialsOf } from '../../lib/format';

// ── card ────────────────────────────────────────────────────
export function Card({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'rounded-card border border-line bg-surface shadow-card',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3',
        className,
      )}
    >
      <div className="space-y-0.5">
        <h3 className="text-sm font-semibold text-heading">{title}</h3>
        {description ? <p className="text-xs text-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function CardBody({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('p-4', className)} {...rest}>
      {children}
    </div>
  );
}

// ── badge ───────────────────────────────────────────────────
export type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

const TONES: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-muted border-line',
  accent: 'bg-accent-subtle text-accent border-accent/20',
  success: 'bg-success-subtle text-success border-success/20',
  warning: 'bg-warning-subtle text-warning border-warning/20',
  danger: 'bg-danger-subtle text-danger border-danger/20',
  info: 'bg-info-subtle text-info border-info/20',
};

export function Badge({
  tone = 'neutral',
  className,
  children,
}: {
  tone?: Tone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-pill border px-2 py-0.5 text-2xs font-medium whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * A status value as a coloured badge.
 *
 * The tone map is passed in by the page rather than guessed from the string:
 * `on_hold` is a warning for a client and neutral for a project, and a shared
 * component that decided for both would be wrong for one of them.
 *
 * The label defaults to a humanized form of the value, so adding a status on
 * the server needs no client change.
 */
export function StatusChip({
  value,
  tones,
  labels,
}: {
  value: string | null | undefined;
  tones?: Record<string, Tone>;
  labels?: Record<string, string>;
}) {
  if (!value) return <span className="text-faint">—</span>;
  const tone = tones?.[value] ?? 'neutral';
  const declared = labels?.[value];
  // A declared label is shown exactly as written. It was phrased by somebody
  // who knew who was reading it — "Waiting on you" is not improved by being
  // rendered "Waiting On You", and casing it here overrules the author.
  //
  // The fallback is a raw enum value, so it gets sentence case: one capital at
  // the front rather than one per word, because `part_paid` is a phrase and
  // not a title. Done in JavaScript rather than with `first-letter:uppercase`,
  // which silently does nothing here — `::first-letter` applies to block
  // containers and a badge is an inline flex box.
  return <Badge tone={tone}>{declared ?? sentenceCase(value)}</Badge>;
}

/** `part_paid` -> `Part paid`. For raw enum values with no declared label. */
function sentenceCase(value: string): string {
  const spaced = value.replace(/[_-]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// ── avatar ──────────────────────────────────────────────────
export function Avatar({
  name,
  src,
  size = 'md',
  className,
}: {
  name: string | null | undefined;
  src?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const sizes = {
    xs: 'size-6 text-2xs',
    sm: 'size-7 text-2xs',
    md: 'size-9 text-xs',
    lg: 'size-12 text-sm',
  } as const;

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-accent-subtle font-semibold text-accent select-none',
        sizes[size],
        className,
      )}
      // The name is the accessible label; the initials inside are decoration,
      // and a screen reader reading "SR" instead of "Sam Reyes" is worse than
      // nothing.
      title={name ?? undefined}
      aria-label={name ?? undefined}
      role="img"
    >
      {src ? (
        <img src={src} alt="" className="size-full object-cover" />
      ) : (
        <span aria-hidden>{initialsOf(name) || '?'}</span>
      )}
    </span>
  );
}

// ── alert ───────────────────────────────────────────────────
const ALERT_ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  danger: AlertCircle,
} as const;

export function Alert({
  tone = 'info',
  title,
  children,
  onDismiss,
  className,
}: {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  title?: ReactNode;
  children?: ReactNode;
  onDismiss?: () => void;
  className?: string;
}) {
  const Icon = ALERT_ICONS[tone];
  const styles = {
    info: 'border-info/25 bg-info-subtle text-info',
    success: 'border-success/25 bg-success-subtle text-success',
    warning: 'border-warning/25 bg-warning-subtle text-warning',
    danger: 'border-danger/25 bg-danger-subtle text-danger',
  } as const;

  return (
    <div
      className={cn('flex gap-3 rounded-card border p-3', styles[tone], className)}
      // An error or a warning interrupts; information waits its turn.
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1 space-y-0.5 text-sm">
        {title ? <p className="font-medium">{title}</p> : null}
        {children ? <div className="text-body/85">{children}</div> : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          className="-m-1 shrink-0 self-start rounded p-1 opacity-70 hover:opacity-100"
          aria-label="Dismiss"
        >
          <X className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}

// ── separator ───────────────────────────────────────────────
export function Separator({ className, label }: { className?: string; label?: string }) {
  if (label) {
    return (
      <div className={cn('flex items-center gap-3', className)}>
        <span className="h-px flex-1 bg-line" />
        <span className="text-2xs font-medium uppercase tracking-wide text-faint">{label}</span>
        <span className="h-px flex-1 bg-line" />
      </div>
    );
  }
  return <hr className={cn('border-line', className)} />;
}

// ── skeleton ────────────────────────────────────────────────
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded bg-surface-2', className)} aria-hidden />;
}

// ── spinner ─────────────────────────────────────────────────
export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent',
        className,
      )}
      role="status"
      aria-label="Loading"
    />
  );
}

// ── label / value ───────────────────────────────────────────
/**
 * One labelled value in a detail grid.
 *
 * An absent value renders an em dash rather than being hidden, so the grid does
 * not reflow between two records that differ only in what they have filled in.
 */
export function DetailField({
  label,
  children,
  className,
}: {
  label: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  const empty = children === null || children === undefined || children === '';
  return (
    <div className={cn('min-w-0 space-y-0.5', className)}>
      <dt className="text-2xs font-medium uppercase tracking-wide text-faint">{label}</dt>
      {/* Wrapped, never cut short: a fact is read in full or not at all —
          a verification note or a scope ended in "…" hides the part that
          mattered, and nothing here offers the rest. */}
      <dd className={cn('break-words text-sm', empty ? 'text-faint' : 'text-body')}>
        {empty ? '—' : children}
      </dd>
    </div>
  );
}

/** The standard layout for a detail grid, so every entity page reads alike. */
export function DetailGrid({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <dl
      className={cn(
        'grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-4',
        className,
      )}
    >
      {children}
    </dl>
  );
}
